import { forwardRef, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import { Workspace } from '@rossum/api-client/workspaces';
import {
  IconChevronDown,
  IconChevronRight,
  IconFolder,
} from '@rossum/ui/icons/tabler';
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  PaperProps,
  SvgIcon,
  SxProps,
  TextField,
  Theme,
} from '@rossum/ui/material';
import { useWorkspacesWithQueues } from '../hooks/useWorkspacesWithQueues';

const PaperComponent = (props: PaperProps) => (
  <Paper elevation={4} {...props} />
);

const RenderGroup = ({
  params,
  workspace,
  defaultOpen = true,
  forceOpen = true,
}: {
  params: AutocompleteRenderGroupParams;
  workspace: Workspace;
  defaultOpen?: boolean;
  forceOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <List key={`group-${params.key}`} dense sx={{ py: 0 }}>
      <ListItemButton onClick={() => setOpen(prevOpen => !prevOpen)}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <SvgIcon fontSize="medium">
            {forceOpen || open ? <IconChevronDown /> : <IconChevronRight />}
          </SvgIcon>
        </ListItemIcon>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <SvgIcon fontSize="medium">
            <IconFolder />
          </SvgIcon>
        </ListItemIcon>
        <ListItemText primary={workspace?.name ?? 'N/A'} />
      </ListItemButton>
      <Collapse in={forceOpen || open} timeout="auto">
        <List component="div" disablePadding>
          {params.children}
        </List>
      </Collapse>
    </List>
  );
};

type QueueSelectProps = {
  disableClearable?: boolean | undefined;
  id?: string;
} & {
  value: Url | null;
  onChange: (values: Url | null) => void;
  sx?: SxProps<Theme>;
  getOptionLabel?: (option: { name: string; workspaceName: string }) => string;
  forceOpenWorkspaces?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
};

export const QueueSelect = forwardRef(
  (
    {
      value,
      onChange,
      sx,
      getOptionLabel,
      forceOpenWorkspaces = true,
      error,
      helperText,
      ...props
    }: QueueSelectProps,
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>('');
    const [userHasSearched, setUserHasSearched] = useState(false);
    const intl = useIntl();

    const { isLoading, workspaces, workspacesWithQueues } =
      useWorkspacesWithQueues({
        enableQueries: true,
      });

    const stableQueues = useMemo(
      () =>
        workspacesWithQueues?.flatMap(ws =>
          ws.queues.map(queue => ({
            ...queue,
            workspaceName: ws.name,
            workspaceId: ws.id,
          }))
        ) ?? [],
      [workspacesWithQueues]
    );

    const stableValue = useMemo(
      () => stableQueues?.find(queue => value === queue.url) ?? null,
      [stableQueues, value]
    );

    return isLoading ? (
      <TextField
        size="small"
        disabled
        label={intl.formatMessage({
          id: 'features.queues.queueSelect.label',
        })}
        placeholder={intl.formatMessage({
          id: 'features.queues.queueSelect.placeholder',
        })}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: <CircularProgress color="inherit" size={20} />,
        }}
        sx={sx}
      />
    ) : (
      <Autocomplete
        data-cy="queue-select"
        PaperComponent={PaperComponent}
        ref={ref}
        size="small"
        options={stableQueues}
        getOptionLabel={
          getOptionLabel ??
          (option => `${option.workspaceName} - ${option.name}`)
        }
        isOptionEqualToValue={(option, selectedValue) =>
          option.url === selectedValue?.url
        }
        groupBy={option => option.workspace ?? ''}
        value={stableValue}
        onOpen={() =>
          setTimeout(() => {
            const el = document.querySelector(
              `[data-url="${stableValue?.url}"]`
            );

            if (el) {
              el.scrollIntoView({ block: 'center' });
            }
          }, 0)
        }
        onChange={(_e, newValue) => {
          onChange(newValue?.url ?? null);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={intl.formatMessage({
              id: 'features.queues.queueSelect.label',
            })}
            placeholder={intl.formatMessage({
              id: 'features.queues.queueSelect.placeholder',
            })}
            InputLabelProps={{ shrink: true }}
            error={error || undefined}
            helperText={helperText || undefined}
          />
        )}
        inputValue={inputValue}
        onInputChange={(_e, newInputValue, reason) => {
          setInputValue(newInputValue);
          if (reason === 'input' && newInputValue !== '') {
            setUserHasSearched(true);
          }
        }}
        onClose={() => setUserHasSearched(false)}
        renderGroup={params => {
          const workspace = workspaces?.find(ws => ws.url === params.group);
          return workspace ? (
            <RenderGroup
              params={params}
              workspace={workspace}
              defaultOpen={
                forceOpenWorkspaces || stableValue?.workspaceId === workspace.id
              }
              forceOpen={userHasSearched}
            />
          ) : null;
        }}
        renderOption={(params, queue) => (
          <ListItemButton
            component="li"
            {...params}
            key={queue.url}
            data-cy="queue-option"
            data-url={queue.url}
            title={queue.name}
            sx={{ scrollMargin: 20 }}
          >
            <ListItemText secondary={queue.name} />
          </ListItemButton>
        )}
        sx={sx}
        {...props}
      />
    );
  }
);
